import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useTranslation } from '../i18n';
import { analytics } from '../hooks/useAnalytics';
import { usePushNotificationToggle } from '../hooks/usePushNotificationToggle';
import { isNativePlatform } from '../lib/runtime';
import { reconcilePushPermission } from '../lib/permissionReconciliation';
import type { NotificationTopic, NotificationPreferences } from '@lotngo/shared';
import { NOTIFICATION_TOPICS, LOCKED_ON_TOPICS } from '@lotngo/shared';

interface TopicDef {
  key:         NotificationTopic;
  labelKey:    string;
  descKey:     string;
  icon:        string;
}

const TOPIC_DEFS: TopicDef[] = [
  { key: 'reclaim_alerts',      labelKey: 'topic_reclaim_alerts',      descKey: 'topic_reclaim_alerts_desc',      icon: 'priority_high' },
  { key: 'my_sessions',         labelKey: 'topic_my_sessions',         descKey: 'topic_my_sessions_desc',         icon: 'directions_car' },
  { key: 'my_spot_activity',    labelKey: 'topic_my_spot_activity',    descKey: 'topic_my_spot_activity_desc',    icon: 'local_parking' },
  { key: 'building_broadcasts', labelKey: 'topic_building_broadcasts', descKey: 'topic_building_broadcasts_desc', icon: 'campaign' },
  { key: 'system_messages',     labelKey: 'topic_system_messages',     descKey: 'topic_system_messages_desc',     icon: 'info' },
];

const LOCKED_SET = new Set(LOCKED_ON_TOPICS);

// Topics that are functionally always-on but intentionally hidden from the
// preferences UI. Server-side enforcement still lives in LOCKED_ON_TOPICS;
// this set only controls visibility.
const HIDDEN_TOPICS = new Set<NotificationTopic>(['system_messages', 'reclaim_alerts']);

export default function NotificationPreferencesPanel() {
  const { t, direction } = useTranslation();
  const qc = useQueryClient();
  const [masterError, setMasterError] = useState<string | null>(null);

  // Token registration + browser permission flow lives in this hook. We
  // borrow it so the master toggle here can do the full "permission →
  // FCM token → POST /subscribe" dance — that's the responsibility absorbed
  // from the old "Receive push notifications" row.
  const { isEnabled, canEnable, isPWAInstalled, disableMutation: _legacyDisable, enableMutation } = usePushNotificationToggle();
  void _legacyDisable; // disable path no longer used here; we keep the token registered when master flips off

  const { data: prefs } = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () =>
      apiClient
        .get<{ data: NotificationPreferences }>('/notifications/preferences')
        .then((r) => r.data.data),
    staleTime: 5 * 60_000,
  });

  // Re-verify the OS permission every time this panel mounts, not just at
  // app launch — catches revoking Notifications in device Settings and
  // coming straight back here. `usePushNotificationToggle`'s `isEnabled`
  // reads the same authStore field reactively, so correcting it here
  // re-renders the toggle immediately with no extra plumbing.
  useEffect(() => {
    void reconcilePushPermission();
  }, []);

  const masterPatchMutation = useMutation({
    mutationFn: (master: boolean) =>
      apiClient
        .patch<{ data: NotificationPreferences }>('/notifications/preferences', { master })
        .then((r) => r.data.data),
    onSuccess: (data, master) => {
      qc.setQueryData(['notifications', 'preferences'], data);
      analytics.trackNotificationMasterToggled(master);
    },
  });

  const topicMutation = useMutation({
    mutationFn: (payload: { topic: NotificationTopic; enabled: boolean }) =>
      apiClient
        .patch<{ data: NotificationPreferences }>('/notifications/preferences', {
          topics: { [payload.topic]: payload.enabled },
        })
        .then((r) => r.data.data),
    onSuccess: (data, { topic, enabled }) => {
      qc.setQueryData(['notifications', 'preferences'], data);
      analytics.trackNotificationTopicToggled(topic, enabled);
    },
  });

  if (!prefs) {
    return (
      <div style={card}>
        <div style={{ padding: '16px 18px', color: '#586064', fontSize: '13px' }}>…</div>
      </div>
    );
  }

  // "Master on" visually requires BOTH a registered device (isEnabled) AND
  // the server-side push_master_enabled flag. Either being false means no
  // notifications can actually reach the user, so the UI shows OFF.
  const masterOn = isEnabled && prefs.master;
  const masterBusy = masterPatchMutation.isPending || enableMutation.isPending;

  async function handleMasterToggle(nextOn: boolean) {
    setMasterError(null);
    try {
      if (nextOn) {
        // Turning ON. Register a device if we don't have a confirmed
        // registration on this platform. On native we ALWAYS re-register:
        // the persisted `isPushNotificationsEnabled` flag may be a stale
        // leftover from a TWA→Capacitor migration where the Web Push token
        // never carried over to the Android FCM channel. POST /subscribe is
        // an idempotent UPSERT keyed on (user_id, building_id) so a
        // redundant call is harmless. On the PWA we keep the original
        // "register only when not yet enabled" behaviour — there's no
        // platform-swap scenario to recover from.
        if (!isEnabled || isNativePlatform()) {
          await enableMutation.mutateAsync();
        }
        await masterPatchMutation.mutateAsync(true);
      } else {
        // Turning OFF. We only flip the server flag — token stays registered
        // so re-enabling is one tap with no permission re-prompt.
        await masterPatchMutation.mutateAsync(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMasterError(msg);
    }
  }

  return (
    <div style={card}>
      {/* Header */}
      <div style={headerWrap}>
        <span
          className="material-symbols-outlined"
          style={{ color: '#005bc0', fontSize: '20px' }}
          aria-hidden
        >
          notifications
        </span>
        <div>
          <div style={headerTitle}>{t('notif_prefs_title')}</div>
          <div style={headerSub}>{t('notif_prefs_subtitle')}</div>
        </div>
      </div>

      {/* Master row. The "install the app" hint is meaningful ONLY on the
          browser PWA where the user hasn't added the app to their home
          screen yet (Web Push requires an installed PWA context on iOS,
          and the install gate keeps UX consistent on Android browsers).
          Inside the Capacitor native shell, the user is already inside the
          installed app — surfacing "Install the app to enable" while their
          toggle is ON looks like a bug. Treat native as
          install-equivalent. */}
      <Row
        labelKey="notif_prefs_master"
        descKey={(isPWAInstalled || isNativePlatform()) ? 'notif_prefs_master_desc' : 'install_app_first'}
        enabled={masterOn}
        disabled={!canEnable || masterBusy}
        onChange={handleMasterToggle}
        accent
        direction={direction}
        t={t}
      />
      {masterError && (
        <div style={{ fontSize: '12px', color: '#dc2626', padding: '4px 0 8px' }}>
          {masterError}
        </div>
      )}

      {/* Topic sub-toggles */}
      <div style={{ borderTop: '1px solid rgba(43,52,55,0.06)', marginTop: '8px' }}>
        {!masterOn && (
          <div style={mutedHelp}>{t('notif_prefs_master_off')}</div>
        )}
        {TOPIC_DEFS
          .filter((td) => NOTIFICATION_TOPICS.includes(td.key))
          .filter((td) => !HIDDEN_TOPICS.has(td.key))
          .map((td) => {
          const locked = LOCKED_SET.has(td.key);
          // Visual rule: when master is off, EVERY sub-toggle renders off,
          // regardless of the stored preference. Locked-on topics follow the
          // master too — if reclaim alerts can't reach the user (master off,
          // no token, etc.), showing them as "on" is misleading.
          const storedEnabled = locked ? true : prefs.topics[td.key];
          const visuallyEnabled = masterOn ? storedEnabled : false;
          return (
            <Row
              key={td.key}
              labelKey={td.labelKey}
              descKey={td.descKey}
              icon={td.icon}
              enabled={visuallyEnabled}
              locked={locked}
              disabled={!masterOn || locked || topicMutation.isPending}
              onChange={(v) => topicMutation.mutate({ topic: td.key, enabled: v })}
              direction={direction}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

interface RowProps {
  labelKey: string;
  descKey:  string;
  icon?:    string;
  enabled:  boolean;
  locked?:  boolean;
  disabled: boolean;
  accent?:  boolean;
  onChange: (v: boolean) => void;
  direction: 'ltr' | 'rtl';
  t: (key: string) => string;
}

function Row({ labelKey, descKey, icon, enabled, locked, disabled, accent, onChange, direction, t }: RowProps) {
  return (
    <div style={rowOuter}>
      {icon && (
        <span
          className="material-symbols-outlined"
          style={{ color: accent ? '#005bc0' : '#586064', fontSize: '20px', flexShrink: 0, marginTop: '2px' }}
          aria-hidden
        >
          {icon}
        </span>
      )}
      <div style={{
        flex: 1,
        [direction === 'rtl' ? 'marginLeft' : 'marginRight']: '12px',
        opacity: disabled && !locked ? 0.4 : 1,
      }}>
        <div style={rowLabelLine}>
          <span style={{ ...rowLabel, ...(accent ? rowLabelAccent : {}) }}>{t(labelKey)}</span>
          {locked && (
            <span style={lockedPill}>
              <span className="material-symbols-outlined" style={{ fontSize: '12px' }} aria-hidden>lock</span>
              {t('notif_prefs_locked')}
            </span>
          )}
        </div>
        <div style={rowDesc}>{t(descKey)}</div>
      </div>
      <button
        role="switch"
        aria-checked={enabled}
        disabled={disabled}
        onClick={() => onChange(!enabled)}
        style={{
          ...toggle,
          backgroundColor: enabled ? '#16a34a' : '#d1d5db',
          opacity: disabled && !locked ? 0.4 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span style={{
          ...toggleThumb,
          transform: enabled
            ? direction === 'rtl' ? 'translateX(-20px)' : 'translateX(20px)'
            : 'translateX(2px)',
        }} />
      </button>
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '14px',
  padding:      '16px 18px',
  boxShadow:    '0 1px 3px rgba(0,0,0,0.05)',
  marginBottom: '24px',
};

const headerWrap: React.CSSProperties = {
  display:    'flex',
  alignItems: 'flex-start',
  gap:        '12px',
  marginBottom: '12px',
};

const headerTitle: React.CSSProperties = {
  fontFamily: "'Manrope', system-ui, sans-serif",
  fontWeight: 700,
  fontSize:   '15px',
  color:      '#111',
  marginBottom: '2px',
};

const headerSub: React.CSSProperties = {
  fontSize: '12px',
  color:    '#6b7280',
};

const rowOuter: React.CSSProperties = {
  display:    'flex',
  alignItems: 'flex-start',
  gap:        '10px',
  paddingTop: '12px',
  paddingBottom: '12px',
  borderTop:  '1px solid rgba(43,52,55,0.04)',
};

const rowLabelLine: React.CSSProperties = {
  display:    'flex',
  alignItems: 'center',
  gap:        '8px',
  marginBottom: '2px',
  flexWrap:   'wrap',
};

const rowLabel: React.CSSProperties = {
  fontSize:   '14px',
  fontWeight: 600,
  color:      '#111',
};

const rowLabelAccent: React.CSSProperties = {
  color: '#005bc0',
};

const rowDesc: React.CSSProperties = {
  fontSize:   '12px',
  color:      '#6b7280',
  lineHeight: 1.4,
};

// Mirrors the existing master-push toggle on ProfilePage so the visual
// language stays consistent in both LTR and RTL.
const toggle: React.CSSProperties = {
  position:    'relative',
  display:     'inline-flex',
  alignItems:  'center',
  width:       '44px',
  height:      '26px',
  borderRadius:'13px',
  border:      'none',
  flexShrink:  0,
  transition:  'background-color 0.2s',
  padding:     0,
};

const toggleThumb: React.CSSProperties = {
  position:        'absolute',
  width:           '22px',
  height:          '22px',
  borderRadius:    '50%',
  backgroundColor: '#fff',
  boxShadow:       '0 1px 3px rgba(0,0,0,0.3)',
  transition:      'transform 0.2s',
};

const lockedPill: React.CSSProperties = {
  display:      'inline-flex',
  alignItems:   'center',
  gap:          '3px',
  background:   '#f1f5f9',
  color:        '#475569',
  fontSize:     '10px',
  fontWeight:   600,
  padding:      '2px 7px',
  borderRadius: '9999px',
};

const mutedHelp: React.CSSProperties = {
  fontSize:  '12px',
  color:     '#9ca3af',
  padding:   '10px 0 6px',
  fontStyle: 'italic',
};
